// Default form values for reset
export const DEFAULT_FORM_VALUES = {
  forceCountry: 'US',
  forcePreferences: [],
  forcePreferencesSelected: {},
  forcePermissions: [],
  forcePermissionsSelected: {},
  forceRussianAccess: false,
  forceTempleViewable: true,
  forceChurchMembership: true,
  forceSignedOut: false,
}

// Convert form values to query parameters for submission
export const convertFormToQueryParams = (values, touchedFields) => {
  const queryParams = new URLSearchParams()

  // Add string values (only if touched)
  if (touchedFields.forceCountry && values.forceCountry) {
    queryParams.append('forceCountry', values.forceCountry)
  }

  // Add forcePreferences (only if touched and have values)
  // Format: setting1:true,setting2:false
  if (touchedFields.forcePreferences && values.forcePreferences?.length > 0) {
    const preferences = values.forcePreferences.map((pref) => {
      // Check if this preference is selected (true) or deselected (false)
      const isSelected = values.forcePreferencesSelected?.[pref] !== false
      return `${pref}:${isSelected}`
    })
    queryParams.append('forcePreferences', preferences.join(','))
  }

  // Add forcePermissions (only if touched and have values)
  // Format: context::perm1,!perm2 (supports negation with !)
  if (touchedFields.forcePermissions && values.forcePermissions?.length > 0) {
    const permissions = values.forcePermissions.map((perm) => {
      // Check if this permission is selected (true) or deselected (false)
      const isSelected = values.forcePermissionsSelected?.[perm]
      return isSelected === false ? `!${perm}` : perm
    })
    queryParams.append('forcePermissions', permissions.join(','))
  }

  // Add boolean values (only if touched, include both true and false)
  if (touchedFields.forceRussianAccess) {
    queryParams.append('forceRussianAccess', values.forceRussianAccess ? 'true' : 'false')
  }
  if (touchedFields.forceTempleViewable) {
    queryParams.append('forceTempleViewable', values.forceTempleViewable ? 'true' : 'false')
  }
  if (touchedFields.forceChurchMembership) {
    queryParams.append('forceChurchMembership', values.forceChurchMembership ? 'true' : 'false')
  }
  if (touchedFields.forceSignedOut) {
    queryParams.append('forceSignedOut', values.forceSignedOut ? 'true' : 'false')
  }

  return queryParams
}

// Convert form values to query param format for real-time URL sync
export const convertFormToQuerySync = (formValues, touchedFields) => {
  const queryUpdates = {}

  // Convert form values to query param format, but only for touched fields
  Object.entries(formValues).forEach(([key, value]) => {
    const formValue = value

    // Only include if field has been touched
    if (!touchedFields[key]) {
      return
    }

    if (Array.isArray(formValue)) {
      // For arrays (forcePreferences, forcePermissions), join with commas or set empty string if empty
      queryUpdates[key] = formValue.length > 0 ? formValue.join(',') : ''
    } else if (typeof formValue === 'boolean') {
      // For booleans, always include as 'true' or 'false'
      queryUpdates[key] = formValue ? 'true' : 'false'
    } else if (typeof formValue === 'object' && formValue !== null) {
      // For selection states (forcePreferencesSelected, forcePermissionsSelected), serialize as "value:true,value:false"
      const entries = Object.entries(formValue).map(([val, selected]) => `${val}:${selected}`)
      queryUpdates[key] = entries.length > 0 ? entries.join(',') : ''
    } else if (typeof formValue === 'string') {
      // For strings, only include if not empty
      if (formValue) {
        queryUpdates[key] = formValue
      }
    }
  })

  return queryUpdates
}

// Helper to build URLSearchParams from an object
export const buildURLSearchParams = (updates) => {
  const params = new URLSearchParams()
  Object.entries(updates).forEach(([key, value]) => {
    if (value !== undefined) {
      if (Array.isArray(value)) {
        params.append(key, value.join(','))
      } else {
        params.append(key, String(value))
      }
    }
  })
  return params
}
